<template>
  <div>
    <retirement-calculator-investment-charts
      id="cfs-calculator-charts"
      class="py-6 px-2 md:p-6 bg-white shadow-lg rounded" />

    <div class="my-12 shadow-lg rounded">
      <input-error
        class="md:hidden pb-6"
        v-if="errorLocations.length > 0 && !errorLocations.includes(selectedTab)"
        error="Select other options" />
      <input-select-box
        class="block md:hidden mb-6 mx-auto min-h-[40px]"
        style="max-width: calc(100% - 40px)"
        :styles="{
          button: 'border-cfs-blue min-h-[70px]',
          arrow: 'text-cfs-blue',
        }"
        name=""
        :options="tabNameArray"
        :item-errors="errorLocations"
        @update-select-box="changeTabFromDropdown" />
      <tab-buttons
        id="calculator-tab-buttons"
        class="hidden md:flex"
        @selected="changeTab"
        :classes="{
          active: 'bg-white border-[#e5ecf9] text-cfs-blue',
          inactive: 'bg-[#e5ecf9] border-[#e5ecf9] hover:bg-white text-cfs-blue',
        }"
        :buttons="formTabs"
        :error-list="errorLocations"
        :selected="selectedTab" />
      <div id="tabs-container" class="bg-white p-8">
        <component
          v-show="!loading"
          :is="formTabs[selectedTab].page"
          :loading="loading"
          @loaded="loading = false"
          @click="validateAndSave"
          :clickable="true" />
      </div>
    </div>

    <div class="flex gap-4 justify-center my-12">
      <custom-button
        @click="downloadRetirementCalcPDF"
        colour="cfs-button"
        :loading="downloadingPDF"
        data-test="downloadPDFButton"
        class="border border-grey-4 rounded-full p-5 w-44 button-2">
        Download
      </custom-button>
      <custom-button
        @click="refreshRetCalc"
        id="start-over-btn"
        data-test="resetCalculator"
        colour="white"
        custom-style="color: #164A9A"
        class="border border-grey-4 rounded-full p-5 w-44 button-2">
        Start again
      </custom-button>
    </div>

    <div
      class="text-f2 md:text-f1 text-cfs-blue rounded shadow-lg bg-white p-6 mt-12"
      v-if="disclaimer">
      <strong>Assumptions and limitations</strong><br />
      <div v-html="disclaimer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import RetirementCalculatorInvestmentCharts from '@/components/CFS/RetirementCalculatorComponents/RetirementCalculatorInvestmentCharts.vue'
import TabButtons from '@/components/BasicInputs/TabButtons.vue'
import { defineAsyncComponent, markRaw, ref, computed } from 'vue'
import CustomButton from '@/components/CustomButton/CustomButton.vue'
import InputError from '@/components/BasicInputs/InputError.vue'
import InputSelectBox from '@/components/BasicInputs/InputSelectBox.vue'
import { downloadSOA } from '@/lib/WhiteLabelHelpers'
import { useRetirementCalculatorStore } from '@/store/pinia/RetirementCalculatorStore'
import { useRetirementCalculatorErrorStore } from '@/store/pinia/RetirementCalculatorErrorStore'
import Highcharts from 'highcharts'

// Emit definition
const emit = defineEmits<{
  click: []
}>()

// Store setup
const retirementCalculatorStore = useRetirementCalculatorStore()
const errorStore = useRetirementCalculatorErrorStore()

// State
const selectedTab = ref(0)
const loading = ref(false)
const downloadingPDF = ref(false)

const formTabs = ref([
  {
    name: 'Super details',
    value: 0,
    page: markRaw(
      defineAsyncComponent(
        () =>
          import('@/components/CFS/RetirementCalculatorComponents/RetirementCalculatorUserData.vue')
      )
    ),
  },
  {
    name: 'Other income + assets',
    value: 1,
    page: markRaw(
      defineAsyncComponent(
        () =>
          import(
            '@/components/CFS/RetirementCalculatorComponents/RetirementCalculatorOtherIncome.vue'
          )
      )
    ),
  },
  {
    name: 'Additional contributions',
    value: 2,
    page: markRaw(
      defineAsyncComponent(
        () =>
          import(
            '@/components/CFS/RetirementCalculatorComponents/RetirementCalculatorContributions.vue'
          )
      )
    ),
  },
  {
    name: 'Career break + part time',
    value: 3,
    page: markRaw(
      defineAsyncComponent(
        () =>
          import(
            '@/components/CFS/RetirementCalculatorComponents/RetirementCalculatorCareerBreak.vue'
          )
      )
    ),
  },
  {
    name: 'Retirement milestones',
    value: 4,
    page: markRaw(
      defineAsyncComponent(
        () =>
          import(
            '@/components/CFS/RetirementCalculatorComponents/RetirementCalculatorMilestones.vue'
          )
      )
    ),
  },
])

// Computed
const disclaimer = computed(() => retirementCalculatorStore.getDisclaimer)
const errorLocations = computed(() => errorStore.getErrorLocations)
const isFormValid = computed(() => errorLocations.value.length === 0)

const tabNameArray = computed(() => {
  return formTabs.value.map((tab) => ({
    value: tab.value,
    label: tab.name,
  }))
})

// Methods
const changeTab = (val: number) => {
  selectedTab.value = val
  loading.value = false // Reset loading state when changing tabs
}

const changeTabFromDropdown = (val: number) => {
  const tab = formTabs.value.findIndex((tab) => tab.value === val)
  changeTab(tab)
}

const validateAndSave = () => {
  emit('click')
}

const refreshRetCalc = async () => {
  await retirementCalculatorStore.refreshRetCalc()
}

const getChartPngs = async (): Promise<string[]> => {
  // filter all the undefined elements from the highcharts array because highcharts
  const charts = Highcharts.charts.filter((chart) => chart !== undefined)
  // map valid ones
  const promises = charts.map((chart) => {
    const chartOptions = {
      exporting: {
        sourceWidth: 600,
        sourceHeight: 400,
      },
    }
    chart.update(chartOptions)
    const svg = chart.getSVG()
    chart.reflow()
    return createImgFromSvg(svg, 'png')
  })
  return await Promise.all(promises)
}

const createImgFromSvg = async (svg: string, imageType: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) {
        reject(new Error('Could not get canvas context'))
        return
      }
      canvas.width = image.width
      canvas.height = image.height
      context.drawImage(image, 0, 0)
      resolve(canvas.toDataURL(`image/${imageType}`))
    }
    image.onerror = (err) => reject(err)
    image.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
  })
}

const downloadRetirementCalcPDF = async () => {
  downloadingPDF.value = true
  await validateAndSave()
  if (!isFormValid.value) {
    errorStore.setErrorLocations(['Please complete all required fields before downloading.'])
    downloadingPDF.value = false
    return
  }

  try {
    const chartPngs = await getChartPngs()
    const response = await retirementCalculatorStore.downloadPDF(chartPngs)
    if (response) {
      downloadSOA(response.data, 'retirement_calc.pdf')
    }
  } catch (err) {
    console.error('Error downloading PDF:', err)
  } finally {
    downloadingPDF.value = false
  }
}
</script>
